import { ProcessEntity } from '@common/enums/processEntity';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import type { IAfwijkingen } from './CardioAfwijkingen';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, InputRadioGroup, InputTextArea } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';
import { NOABNORMALITY } from '../../utils/v1/Icd10/Icd10Selection';
import { AddEditAfwijkingen, getEmptyAfwijkingen } from './CardioAfwijkingen';

interface IInnerTccrV1DiagnosticReportAddEditState {
    buttonDisabled?: boolean;
}

interface ICustomControlData extends IAfwijkingen {
    aanvullende_vragen: string | null;
    advies: string | null;
    bezoek_specialist_nodig: 'Ja' | 'Nee' | 'Zie beleid' | null;
    conclusie: string | null;
    conclusie_diagnose: ConclusieDiagnoseOption;
}

export function TccrV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    return <InnerTccrV1DiagnosticReportAddEdit {...props} />;
}

export class InnerTccrV1DiagnosticReportAddEdit extends Component<
    ICustomControlDiagnosticReportAddEditProps,
    IInnerTccrV1DiagnosticReportAddEditState
> {
    public render() {
        return (
            <ProcessContainer>
                <ProcessItemHeader
                    isFirstItem={this.props.isFirstItem}
                    isItemOpen
                    organizationName={this.props.currentDiagnosticReport?.performerOrganization?.name}
                    practitionerName={
                        this.props.currentDiagnosticReport && this.props.currentDiagnosticReport.performerPractitioner
                            ? getDisplayName(this.props.currentDiagnosticReport.performerPractitioner)
                            : undefined
                    }
                    process={this.props.process}
                    title={translateKey('diagnosticReport.category_consultReport')}
                />

                <Form onSubmit={() => null}>
                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.conclusie')}>
                        <FormLabel isRequired>{translateKey('diagnosticReport.findings')}</FormLabel>
                        <InputContainer>
                            <InputRadioGroup
                                automation="conclusion"
                                error={this.props.errors.get('customControlData.conclusie')}
                                name="conclusie"
                                onChange={async (value) => {
                                    let newDiagnosisConsiderationIcd10 = null;
                                    if (value === 'Normaal') {
                                        newDiagnosisConsiderationIcd10 = NOABNORMALITY;
                                    } else if (
                                        this.props.diagnosticReport.diagnosis?.diagnosisConsiderationIcd10 !==
                                        NOABNORMALITY
                                    ) {
                                        newDiagnosisConsiderationIcd10 =
                                            this.props.diagnosticReport.diagnosis?.diagnosisConsiderationIcd10 || null;
                                    }

                                    await this.handleChangeCustomControl({
                                        conclusie: value,
                                        afwijking_in:
                                            value !== 'Afwijkend'
                                                ? getEmptyAfwijkingen().afwijking_in
                                                : this.props.diagnosticReport.customControlData.afwijking_in,
                                        afwijking_details:
                                            value !== 'Afwijkend'
                                                ? getEmptyAfwijkingen().afwijking_details
                                                : this.props.diagnosticReport.customControlData.afwijking_details,
                                    });
                                    await this.props.handleChange('diagnosis', {
                                        ...this.props.diagnosticReport.diagnosis,
                                        diagnosisConsiderationIcd10: newDiagnosisConsiderationIcd10,
                                    });
                                }}
                                onDoValidate={async () => await this.validateOneCustomControl('conclusie')}
                                options={['Normaal', 'Afwijkend', 'Niet te beoordelen']}
                                value={this.props.diagnosticReport.customControlData.conclusie}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    {this.props.diagnosticReport.customControlData.conclusie &&
                        this.props.diagnosticReport.customControlData.conclusie === 'Afwijkend' && (
                            <AddEditAfwijkingen
                                afwijkingen={this.props.diagnosticReport.customControlData}
                                errors={this.props.errors}
                                onChange={async (value) => await this.handleChangeCustomControl({ ...value })}
                                onDoValidate={async (name, category, disorder) => {
                                    const afwijkingenName =
                                        name + (disorder ? `.${category}.${disorder}` : `.${category}`);
                                    await this.props.validateOneCustomControl(afwijkingenName);
                                }}
                            />
                        )}

                    <DiagnosisConclusionSelection
                        conclusie_diagnose={this.props.diagnosticReport.customControlData.conclusie_diagnose}
                        diagnosis={this.props.diagnosticReport.diagnosis}
                        errors={this.props.errors}
                        handleChangeConclusieDiagnose={this.props.handleChangeCustomControl}
                        handleChangeDiagnosisConsideration={async (diagnosis) =>
                            await this.props.handleChange('diagnosis', diagnosis)
                        }
                        isRequired
                        service={this.props.service}
                        validateConclusieDiagnose={this.props.validateOneCustomControl}
                        validateDiagnosisConsideration={async () => this.props.validateOne('diagnosis')}
                    />

                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.bezoek_specialist_nodig')}>
                        <FormLabel isRequired>Bezoek aan specialist nu nodig?</FormLabel>
                        <InputContainer>
                            <InputRadioGroup
                                automation="visitSpecialist"
                                error={this.props.errors.get('customControlData.bezoek_specialist_nodig')}
                                name="bezoek_specialist_nodig"
                                onChange={async (value) =>
                                    await this.handleChangeCustomControl({ bezoek_specialist_nodig: value })
                                }
                                onDoValidate={async () => {
                                    await this.validateOneCustomControl('bezoek_specialist_nodig');
                                    if (
                                        this.props.diagnosticReport.customControlData.bezoek_specialist_nodig !==
                                        'Zie beleid'
                                    ) {
                                        await this.validateOneCustomControl('advies');
                                    }
                                }}
                                options={['Ja', 'Nee', 'Zie beleid']}
                                value={this.props.diagnosticReport.customControlData.bezoek_specialist_nodig}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.advies')}>
                        <FormLabel
                            isRequired={
                                this.props.diagnosticReport.customControlData.bezoek_specialist_nodig === 'Zie beleid'
                            }
                        >
                            {translateKey('diagnosticReport.policy')}
                        </FormLabel>
                        <InputContainer>
                            <InputTextArea
                                autoResize={true}
                                error={this.props.errors.get('customControlData.advies')}
                                onChange={async (value) => await this.handleChangeCustomControl({ advies: value })}
                                onDoValidate={async () => await this.validateOneCustomControl('advies')}
                                placeholder={translateKey('diagnosticReport.policyPlaceholder')}
                                value={this.props.diagnosticReport.customControlData.advies}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                        <FormLabel>{translateKey('common.remarks')}</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                automation="remark"
                                autoResize={true}
                                error={this.props.errors.get('remark')}
                                onChange={async (value) => await this.props.handleChange('remark', value)}
                                onDoValidate={async () => await this.props.validateOne('remark')}
                                placeholder={translateKey('common.remarksOrQuestions')}
                                value={this.props.diagnosticReport.remark}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.diagnosticReport}
                                isExisting={!!this.props.request.id}
                                revertChanges={this.props.revertDiagnosticReportChanges}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveDiagnosticReport('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveDiagnosticReport('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </ProcessContainer>
        );
    }

    private async handleChangeCustomControl(value: Partial<ICustomControlData>) {
        await this.props.handleChangeCustomControl(value);
    }

    private async validateOneCustomControl(name: keyof ICustomControlData) {
        await this.props.validateOneCustomControl(name);
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedDiagnosticReport = await this.props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const diagnosticReportId = await this.props.onSaveDiagnosticReport(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', this.props.request.requesterOrganization?.name)
            );
        }

        await this.setState({ buttonDisabled: false });
        await this.props.onSave(diagnosticReportId);
    }
}

export function tccrV1DiagnosticReportInitializeCustomControlData() {
    return {
        ...getEmptyAfwijkingen(),
        conclusie: null,
        conclusie_diagnose: null,
        bezoek_specialist_nodig: null,
        advies: null,
        aanvullende_vragen: null,
    };
}
